/**
 * Sub-hook for PO Lines export and import template functionality (CR-1558).
 */
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { purchasingAPI, POLineExportTemplate } from '../../../../lib/api';
import { useView } from '../../../../contexts/ViewContext';
import { POLinesFilters } from './types';

interface UsePOLinesExportParams {
  filters: POLinesFilters;
  activeTab: string;
}

export function usePOLinesExport({ filters, activeTab }: UsePOLinesExportParams) {
  const { getViewQueryParams } = useView();

  const [exporting, setExporting] = useState(false);
  // Company-shared, admin-managed export templates (active only).
  const [templates, setTemplates] = useState<POLineExportTemplate[]>([]);

  useEffect(() => {
    let cancelled = false;
    purchasingAPI.getExportTemplates({ is_active: true, page_size: 200 })
      .then(res => {
        if (cancelled) return;
        const data = res.data;
        const list = Array.isArray(data)
          ? data
          : (data as { results: POLineExportTemplate[] }).results ?? [];
        setTemplates(list);
      })
      .catch(() => { /* templates are optional — silent on failure */ });
    return () => { cancelled = true; };
  }, []);

  /**
   * Build query params that map the current filter/tab state to API filter keys.
   * Used by both export and import-template download.
   */
  const buildFilterParams = useCallback((_forExport: boolean = false): Record<string, string> => {
    const queryParams: Record<string, string> = {};

    if (filters.poNumber) queryParams['po__po_number__icontains'] = filters.poNumber;
    if (filters.lineNumber) queryParams['line_number'] = filters.lineNumber;
    if (filters.customer) queryParams['po__customer'] = filters.customer;
    else if (filters.customerSearch) queryParams['po__customer__name__icontains'] = filters.customerSearch;
    if (filters.supplier) queryParams['po__supplier'] = filters.supplier;
    if (filters.partNumber) queryParams['part_number__icontains'] = filters.partNumber;
    if (filters.description) queryParams['description__icontains'] = filters.description;

    // Status filter targets processing_stage (the visible "Status" column),
    // mirroring usePOLinesState so exports match the on-screen filter.
    if (filters.status) {
      queryParams['processing_stage'] = filters.status;
    }

    if (filters.on_hold === 'yes') queryParams['on_hold'] = 'true';
    else if (filters.on_hold === 'no') queryParams['on_hold'] = 'false';
    if (filters.buyerId) queryParams['buyer_id__icontains'] = filters.buyerId;
    if (filters.batchSerialNo) queryParams['batch_serial_no__icontains'] = filters.batchSerialNo;
    if (filters.otifOverridden === 'yes') queryParams['otif_overridden'] = 'true';
    else if (filters.otifOverridden === 'no') queryParams['otif_overridden'] = 'false';
    if (filters.customerGroup) queryParams['po__customer__group__name__icontains'] = filters.customerGroup;
    if (filters.oddFrom) queryParams['original_delivery_date__gte'] = filters.oddFrom;
    if (filters.oddTo) queryParams['original_delivery_date__lte'] = filters.oddTo;
    if (filters.ppv === 'positive') queryParams['ppv__gt'] = '0';
    else if (filters.ppv === 'negative') queryParams['ppv__lt'] = '0';
    else if (filters.ppv === 'zero') queryParams['ppv'] = '0';
    if (filters.commodityGroup) queryParams['commodity_group'] = filters.commodityGroup;
    if (filters.commodityType) queryParams['commodity_type'] = filters.commodityType;

    // Tab-based filtering
    if (activeTab === 'open') queryParams['status__in'] = 'pending,partially_received';
    else if (activeTab === 'closed') queryParams['status'] = 'received';
    else if (activeTab === 'on_hold') {
      // On Hold = on hold AND still open (mirror of usePOLinesState)
      queryParams['on_hold'] = 'true';
      queryParams['status__in'] = 'pending,partially_received';
    }
    else if (activeTab === 'needs_action') queryParams['needs_action'] = 'true';
    else if (activeTab === 'high_priority') {
      // Open lines flagged high OR critical priority (mirror of usePOLinesState).
      queryParams['status__in'] = 'pending,partially_received';
      queryParams['priority__in'] = 'high,critical';
    }

    return queryParams;
  }, [filters, activeTab]);

  /**
   * Trigger a download of the export, optionally using a saved template.
   *
   * @param format - xlsx (default) or csv
   * @param templateId - optional saved template id; restricts/orders columns
   */
  const runExport = useCallback(async (
    format: 'xlsx' | 'csv',
    templateId?: number,
  ) => {
    try {
      setExporting(true);

      const queryParams = buildFilterParams(true);
      queryParams['file_format'] = format;
      if (templateId != null) queryParams['template_id'] = String(templateId);

      // View context (site / group / company) — without these the export
      // pulls everything the user has access to, ignoring the top-bar
      // site selector.
      const viewParams = getViewQueryParams();
      if (viewParams['site'])    queryParams['site']    = String(viewParams['site']);
      if (viewParams['group'])   queryParams['group']   = String(viewParams['group']);
      if (viewParams['company']) queryParams['company'] = String(viewParams['company']);

      const response = await purchasingAPI.exportLines(queryParams);
      const blob = new Blob([response.data], {
        type: format === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'text/csv'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `po_lines_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Export completed successfully');
    } catch (err: unknown) {
      console.error('Export failed:', err);
      toast.error('Failed to export PO lines');
    } finally {
      setExporting(false);
    }
  }, [buildFilterParams, getViewQueryParams]);

  // Full export (all columns) — CR-1558.
  const handleExport = useCallback((format: 'xlsx' | 'csv' = 'xlsx') => {
    return runExport(format);
  }, [runExport]);

  // Template-driven export (selected columns in saved order).
  const handleTemplateExport = useCallback((
    templateId: number,
    format: 'xlsx' | 'csv' = 'xlsx',
  ) => {
    return runExport(format, templateId);
  }, [runExport]);

  return {
    exporting,
    handleExport,
    handleTemplateExport,
    exportTemplates: templates,
  };
}
